import {
  createContext,
  useContext,
  useCallback,
  useState,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';

export enum WindowId {
  Terminal = 'terminal',
  Browser = 'browser',
  Finder = 'finder',
  Notepad = 'notepad',
  Paint = 'paint',
}

type WindowState = 'open' | 'minimized' | 'closed' | 'maximized';

interface WindowInfo {
  id: string;
  title: string;
  icon: ReactNode;
  state: WindowState;
  prevState?: WindowState;
}

interface WindowManagerContextValue {
  windows: WindowInfo[];
  focusedWindow: WindowInfo | null;
  registerWindow: (id: string, title: string, icon: ReactNode, initialState?: WindowState) => void;
  getZIndex: (windowId: string) => number;
  bringToFront: (windowId: string) => void;
  focusDesktop: () => void;
  getWindowState: (windowId: string) => WindowState;
  closeWindow: (windowId: string) => void;
  minimizeWindow: (windowId: string) => void;
  maximizeWindow: (windowId: string) => void;
  restoreWindow: (windowId: string) => void;
  openWindow: (windowId: string) => void;
}

const WindowManagerContext = createContext<WindowManagerContextValue | null>(null);

const BASE_Z_INDEX = 10;

interface WindowManagerProviderProps {
  children: ReactNode;
}

export function WindowManagerProvider({ children }: WindowManagerProviderProps) {
  const [windows, setWindows] = useState<WindowInfo[]>([]);
  const [windowStack, setWindowStack] = useState<string[]>([]);
  const [desktopFocused, setDesktopFocused] = useState(false);

  const registerWindow = useCallback((id: string, title: string, icon: ReactNode, initialState: WindowState = 'open') => {
    setWindows(prev => {
      if (prev.find(w => w.id === id)) return prev;
      return [...prev, { id, title, icon, state: initialState }];
    });
    setWindowStack(prev => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
  }, []);

  const bringToFront = useCallback((windowId: string) => {
    setDesktopFocused(false);
    setWindowStack(prev => {
      const filtered = prev.filter(id => id !== windowId);
      return [...filtered, windowId];
    });
  }, []);

  const focusDesktop = useCallback(() => {
    setDesktopFocused(true);
  }, []);

  const getZIndex = useCallback((windowId: string) => {
    const index = windowStack.indexOf(windowId);
    if (index === -1) return BASE_Z_INDEX;
    return BASE_Z_INDEX + index;
  }, [windowStack]);

  const getWindowState = useCallback((windowId: string): WindowState => {
    const window = windows.find(w => w.id === windowId);
    return window?.state ?? 'closed';
  }, [windows]);

  const closeWindow = useCallback((windowId: string) => {
    setWindows(prev =>
      prev.map(w => (w.id === windowId ? { ...w, state: 'closed' as WindowState, prevState: w.state } : w))
    );
  }, []);

  const minimizeWindow = useCallback((windowId: string) => {
    setWindows(prev =>
      prev.map(w => (w.id === windowId ? { ...w, state: 'minimized' as WindowState, prevState: w.state } : w))
    );
  }, []);

  const maximizeWindow = useCallback((windowId: string) => {
    setWindows(prev =>
      prev.map(w => {
        if (w.id !== windowId) return w;
        // Toggle maximize
        if (w.state === 'maximized') {
          return { ...w, state: 'open' as WindowState };
        }
        return { ...w, state: 'maximized' as WindowState, prevState: w.state };
      })
    );
    bringToFront(windowId);
  }, [bringToFront]);

  const restoreWindow = useCallback((windowId: string) => {
    setWindows(prev =>
      prev.map(w => (w.id === windowId ? { ...w, state: 'open' as WindowState } : w))
    );
    bringToFront(windowId);
  }, [bringToFront]);

  const openWindow = useCallback((windowId: string) => {
    setWindows(prev =>
      prev.map(w => (w.id === windowId ? { ...w, state: 'open' as WindowState } : w))
    );
    bringToFront(windowId);
  }, [bringToFront]);

  const focusedWindow = useMemo(() => {
    if (desktopFocused) return null;
    for (let i = windowStack.length - 1; i >= 0; i--) {
      const win = windows.find(w => w.id === windowStack[i]);
      if (win && (win.state === 'open' || win.state === 'maximized')) {
        return win;
      }
    }
    return null;
  }, [windowStack, windows, desktopFocused]);

  return (
    <WindowManagerContext.Provider
      value={{
        windows,
        focusedWindow,
        registerWindow,
        getZIndex,
        bringToFront,
        focusDesktop,
        getWindowState,
        closeWindow,
        minimizeWindow,
        maximizeWindow,
        restoreWindow,
        openWindow,
      }}
    >
      {children}
    </WindowManagerContext.Provider>
  );
}

type WindowInitialState = 'open' | 'closed';

export function useWindowManager(windowId: string, title?: string, icon?: ReactNode, initialState: WindowInitialState = 'open') {
  const context = useContext(WindowManagerContext);
  if (!context) {
    throw new Error('useWindowManager must be used within a WindowManagerProvider');
  }

  const {
    registerWindow,
    getZIndex,
    bringToFront,
    getWindowState,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
    openWindow,
  } = context;

  // Register window on mount
  useEffect(() => {
    if (title && icon) {
      registerWindow(windowId, title, icon, initialState);
    }
  }, [windowId, title, icon, initialState, registerWindow]);

  const zIndex = getZIndex(windowId);
  const state = getWindowState(windowId);
  const focus = useCallback(() => bringToFront(windowId), [bringToFront, windowId]);
  const close = useCallback(() => closeWindow(windowId), [closeWindow, windowId]);
  const minimize = useCallback(() => minimizeWindow(windowId), [minimizeWindow, windowId]);
  const maximize = useCallback(() => maximizeWindow(windowId), [maximizeWindow, windowId]);
  const restore = useCallback(() => restoreWindow(windowId), [restoreWindow, windowId]);
  const open = useCallback(() => openWindow(windowId), [openWindow, windowId]);

  return {
    zIndex,
    state,
    focus,
    close,
    minimize,
    maximize,
    restore,
    open,
    isOpen: state === 'open',
    isMinimized: state === 'minimized',
    isClosed: state === 'closed',
    isMaximized: state === 'maximized',
  };
}

export function useFocusedWindow() {
  const context = useContext(WindowManagerContext);
  return context?.focusedWindow ?? null;
}

export function useDesktopFocus() {
  const context = useContext(WindowManagerContext);
  if (!context) {
    throw new Error('useDesktopFocus must be used within a WindowManagerProvider');
  }
  return context.focusDesktop;
}

export function useDock() {
  const context = useContext(WindowManagerContext);
  if (!context) {
    throw new Error('useDock must be used within a WindowManagerProvider');
  }

  return {
    windows: context.windows,
    openWindow: context.openWindow,
    restoreWindow: context.restoreWindow,
  };
}
